import * as moment from 'moment';
import 'moment-duration-format';
import { roundNumber } from '.';
import { timeValues, timeMetrics } from '../Constants/timeFormats';

// formatters
export const getCurrentDate = () => moment();
export const getCurrentTimestamp = () => moment().unix();
export const getCurrentFormattedDate = format => moment().format(format);
export const getFormattedDateFromTimestamp = (timestamp, format) => moment.unix(timestamp).format(format);
export const getFormattedDateFromDateObject = (date, format) => moment(date).format(format);
export const getFormattedDateFromMomentObject = (date, format) => date.format(format);
export const parseToMoment = date => moment(date);

// 01:23:45, 01-23-45
export const getCustomSeparatedTime = (seconds, separator) => {
    let values = getTimeValues(seconds);
    let resultString = `${values[0]}`;

    if (values.length > 1) for (let i = 1; i < values.length; i++) resultString += `${separator}${values[i]}`;
    else resultString = `00${separator}${resultString}`;

    return resultString;
};

//
export const getTimeValues = seconds => {
    let resultArr = [];
    const timeValues = { ...getTimeValuesFromSeconds(seconds) };

    timeMetrics.forEach(metric => {
        if (timeValues[metric] > 0) resultArr.push(addLeadingZero(timeValues[metric].toString()));
        else return;
    });

    return resultArr;
};

export const addLeadingZero = str => {
    const stringified = str.toString();
    return stringified.length < 2 ? `0${stringified}` : stringified;
};

export const displayTime = seconds => {
    let resultString = '';

    const timeValues = { ...getTimeValuesFromSeconds(seconds) };
    timeMetrics.forEach(metric => {
        if (timeValues[metric] > 0) resultString += `${timeValues[metric]}${metric[0]} `;
        else return;
    });

    return resultString.trim();
};

const getTimeValuesFromSeconds = seconds => {
    let days = Math.floor(seconds / timeValues.DAY);
    seconds -= days * timeValues.DAY;
    let hours = Math.floor(seconds / timeValues.HOUR);
    seconds -= hours * timeValues.HOUR;
    let minutes = Math.floor(seconds / timeValues.MINUTE);
    seconds -= minutes * timeValues.MINUTE;

    return { days, hours, minutes, seconds };
};

export const displaySelectedTime = seconds => {
    let time = displayTime(seconds).split(' ');

    if (time.length > 1) return `${time[0]} ${time[1]}`;
    else return time[0];
};

export const getFormattedTimePassed = time => {
    const now = moment(moment().unix());
    const before = moment(moment(time));

    let difference = moment.duration(now.diff(before), 'seconds').format(defineFormat(now.diff(before)));
    return `${difference} ago`;
};

const defineFormat = timeDifference => {
    switch (true) {
        case timeDifference >= timeValues.YEAR:
            return 'y [years]';
        case timeDifference >= timeValues.MONTH:
            return 'M [months]';
        case timeDifference >= timeValues.DAY:
            return 'd [days]';
        case timeDifference >= timeValues.HOUR:
            return 'h [hours]';
        case timeDifference >= timeValues.MINUTE:
            return 'm [minutes]';
        default:
            return 's [seconds]';
    }
};

const substractMinutes = (date, minutes) => {
    return moment(date)
        .subtract(minutes, 'm')
        .toDate();
};

export const convertDateToServerDateTypeMoment = date => {
    return (
        moment(date)
            // .utc()
            .format('YYYY-MM-DD')
    );
};

export const formatDateToServerType = date => {
    let res = moment(date)
        .utc()
        .format('YYYY-MM-DD HH:mm:ss');
    return res;
};

export const convertTimestampToMomentObject = timestamp => {
    return moment.unix(timestamp);
};

export const serverDateTimeToUtc = date => {
    return moment(date)
        .utc()
        .format('YYYY-MM-DD HH:mm:ss');
};

export const getClassCancellationDate = (date, option) => {
    let dateObj = new Date(date.replace(/-/g, '/'));

    switch (parseInt(option)) {
        case 0: {
            return formatDateToServerType(moment(dateObj).toDate());
        }
        case 1: {
            return formatDateToServerType(moment(dateObj).toDate());
        }
        case 2:
            return formatDateToServerType(substractMinutes(dateObj, 30));
        case 3:
            return formatDateToServerType(substractMinutes(dateObj, 60));
        case 4:
            return formatDateToServerType(substractMinutes(dateObj, 120));
        case 5:
            return formatDateToServerType(substractMinutes(dateObj, 240));
        case 6:
            return formatDateToServerType(substractMinutes(dateObj, 720));
        case 7:
            return formatDateToServerType(substractMinutes(dateObj, 1440));
        case 8:
            return formatDateToServerType(substractMinutes(dateObj, 2880));
        case 9:
            return formatDateToServerType(substractMinutes(dateObj, 10080));
        default:
            return formatDateToServerType(new Date());
    }
};

export const addDay = date => {
    return date.setDate(date.getDate() + 1);
};

export const substractDay = date => {
    return date.setDate(date.getDate() - 1);
};

export const formatDateForServer = date => {
    return `${date.getFullYear()}-${addDateLeadingZero(date.getMonth() + 1)}-${addDateLeadingZero(date.getDate())}`;
};

export const getCreatedDateForWorkout = timestamp => {
    if (!timestamp && typeof timestamp !== 'number') return null;

    const now = moment();
    const created = moment.unix(timestamp);

    const sec = now.diff(created, 'seconds');
    const min = now.diff(created, 'minutes');
    const hours = now.diff(created, 'hours');
    const days = now.diff(created, 'days');
    const months = now.diff(created, 'month');
    const years = now.diff(created, 'years');

    if (years > 0) return years === 1 ? `${years} year` : `${years}years`;
    if (months > 0) return months === 1 ? `${months} m` : `${months}m`;
    if (days > 0) return days === 1 ? `${days} d` : `${days}d`;
    if (hours > 0) return hours === 1 ? `${hours} hour` : `${hours}h`;
    if (min > 0) return min === 1 ? `${min} m` : `${min}m`;
    if (sec > 0) return sec === 1 ? `${sec} s` : `${sec}s`;

    return 'Now';
};

const addDateLeadingZero = str => {
    let stringified = str.toString();
    return stringified.length < 2 ? `0${stringified}` : stringified;
};

export const convertSecondsToMinutes = seconds => {
    return roundNumber(parseInt(seconds) / 60, 1);
};

export const secondsMinutesFormat = seconds => {
    let minutes = Math.floor(seconds / timeValues.MINUTE);
    seconds -= minutes * timeValues.MINUTE;

    return `${addLeadingZero(minutes)}:${addLeadingZero(seconds)}`;
};

export const secondsHoursFormat = seconds => {
    let hours = Math.floor(seconds / timeValues.HOUR);
    seconds -= hours * timeValues.HOUR;
    let minutes = Math.floor(seconds / timeValues.MINUTE);
    seconds -= minutes * timeValues.MINUTE;

    return `${addLeadingZero(hours)}:${addLeadingZero(minutes)}:${addLeadingZero(seconds)}`;
};

export const getCurrentWeekNumber = () => {
    return moment().isoWeek();
};

export const getCurrentWeekDayNumber = () => {
    return moment().isoWeekday();
};

export const getFirstWeekDay = offset => {
    if (offset)
        return moment()
            .add(offset, 'weeks')
            .startOf('week')
            .add(1, 'day');

    return moment()
        .startOf('week')
        .add(1, 'day');
};

export const getLastWeekDay = offset => {
    if (offset)
        return moment()
            .add(offset, 'weeks')
            .endOf('week')
            .add(1, 'day');

    return moment()
        .endOf('week')
        .add(1, 'day');
};

export const formatWithDayOrder = date => {
    return moment.isMoment(date) ? date.format('Do MMM') : 'Wrong date';
};

export const isDateToday = date => {
    return moment(date).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');
};

export const getTimestampFromMoment = date => {
    return moment(date).unix();
};

export const getTomorrow = () => {
    return moment()
        .add(1, 'day')
        .format('YYYY-MM-DD');
};

export const getParticularWeekDay = (offsetWeeks, offsetDays) => {
    return moment()
        .add(offsetWeeks, 'weeks')
        .startOf('week')
        .add(offsetDays, 'days');
};

export const convertTimeToNumericFormat = timeSeconds => {
    let seconds = Number(timeSeconds);

    let hours = addLeadingZero(Math.floor(seconds / timeValues.HOUR));
    seconds -= hours * timeValues.HOUR;
    let minutes = addLeadingZero(Math.floor(seconds / timeValues.MINUTE));
    seconds -= minutes * timeValues.MINUTE;
    seconds = addLeadingZero(seconds);

    return timeSeconds >= timeValues.HOUR ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
};



// WEBPACK FOOTER //
// ./src/Utils/timeConvertorHelper.js